import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import "./AccountTable.css"; // CSS import karein

import {
  Button,
  CircularProgress,
  Alert,
  Menu,
  MenuItem,
  ButtonGroup,
  TextField,
  InputAdornment,
} from "@mui/material";

import Header from "../../../header/header";
import Navvbar from "../../../header/Navvbar";
import Footer from "./../../../footer";
import Sidebar from "../../../Sidebar";

import { MdArrowDropDown, MdDelete } from "react-icons/md";
import { FaPlus } from "react-icons/fa";
import { FiPrinter } from "react-icons/fi";
import { TbFileDownload } from "react-icons/tb";

import Swal from "sweetalert2";

const AccountTable = () => {
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { accountId } = useParams();

  const [anchorEl, setAnchorEl] = useState(null);
  const [labId, setLabId] = useState(null);
  const [userType, setUserType] = useState("lab");

  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const navigate = useNavigate();

  const [isDisabled, setIsDisabled] = useState(true);

  // =========================================================
  // PAGINATION
  // =========================================================

  const rowsPerPage = 50;
  const [currentPage, setCurrentPage] = useState(1);

  // =========================================================
  // FILTERED ROWS
  // =========================================================

  const filteredRows = useMemo(() => {
    return rows.filter((row) =>
      Object.values(row).some((value) =>
        value
          ?.toString()
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    );
  }, [rows, searchTerm]);

  // =========================================================
  // PAGINATION CALCULATIONS
  // =========================================================

  const totalEntries = filteredRows.length;
  const totalPages = totalEntries > 0 ? Math.ceil(totalEntries / rowsPerPage) : 1;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalEntries);
  const paginatedRows = filteredRows.slice(startIndex, endIndex);

  // =========================================================
  // RESET PAGE WHEN SEARCH CHANGES
  // =========================================================

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // =========================================================
  // GET LAB ID
  // =========================================================

  useEffect(() => {
    const storedLabId = localStorage.getItem("userLabId");
    const storedUserType = localStorage.getItem("userType");

    if (storedLabId) {
      setLabId(storedLabId);
    }

    if (storedUserType) {
      setUserType(storedUserType);
    } else if (storedLabId) {
      setUserType("lab");
    }
  }, []);

  // =========================================================
  // CSV CONVERTER
  // =========================================================

  const convertToCSV = (data, columns) => {
    if (!data || data.length === 0) {
      return "";
    }

    const headers = columns.map((col) => `"${col.headerName}"`).join(",");

    const csvRows = data.map((row) => {
      return columns
        .map((col) => {
          const value = row[col.field] || "";
          return `"${String(value).replace(/"/g, '""')}"`;
        })
        .join(",");
    });

    return [headers, ...csvRows].join("\n");
  };

  // =========================================================
  // DOWNLOAD CSV
  // =========================================================

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // =========================================================
  // EXPORT TO EXCEL
  // =========================================================

  const handleExportToExcel = () => {
    const columnsForExport = [
      { field: "accountId", headerName: "Account ID" },
      { field: "account_name", headerName: "Name" },
      { field: "address", headerName: "Address" },
      { field: "address2", headerName: "Address2" },
      { field: "city", headerName: "City" },
      { field: "state", headerName: "State" },
      { field: "zip", headerName: "Zip Code" },
      { field: "country", headerName: "Country" },
      { field: "telephone", headerName: "Telephone" },
      { field: "email", headerName: "Email" },
      { field: "website", headerName: "Website" },
      { field: "time_zone", headerName: "Timezone" },
      { field: "status", headerName: "Status" },
      { field: "account_type", headerName: "Account Type" },
      { field: "addedBy", headerName: "Created By Lab" },
    ];

    const csvData = convertToCSV(filteredRows, columnsForExport);
    downloadCSV(csvData, `my_accounts_export_${new Date().toISOString().split("T")[0]}.csv`);
    handleClose();
  };

  // =========================================================
  // EXPORT BILLING ADDRESSES
  // =========================================================

  const handleExportBillingAddresses = () => {
    const billingColumns = [
      { field: "account_name", headerName: "Name" },
      { field: "address", headerName: "Address" },
      { field: "address2", headerName: "Address2" },
      { field: "city", headerName: "City" },
      { field: "state", headerName: "State" },
      { field: "zip", headerName: "Zip Code" },
      { field: "country", headerName: "Country" },
      { field: "status", headerName: "Status" },
    ];

    const csvData = convertToCSV(filteredRows, billingColumns);
    downloadCSV(csvData, `billing_addresses_${new Date().toISOString().split("T")[0]}.csv`);
    handleClose();
  };

  // =========================================================
  // EXPORT SHIPPING ADDRESSES
  // =========================================================

  const handleExportShippingAddresses = () => {
    const shippingColumns = [
      { field: "account_name", headerName: "Name" },
      { field: "address", headerName: "Address" },
      { field: "address2", headerName: "Address2" },
      { field: "city", headerName: "City" },
      { field: "state", headerName: "State" },
      { field: "zip", headerName: "Zip Code" },
      { field: "country", headerName: "Country" },
      { field: "status", headerName: "Status" },
    ];

    const csvData = convertToCSV(filteredRows, shippingColumns);
    downloadCSV(csvData, `shipping_addresses_${new Date().toISOString().split("T")[0]}.csv`);
    handleClose();
  };

  // =========================================================
  // FETCH ACCOUNTS
  // =========================================================

  const fetchAccounts = async () => {
    if (!labId) {
      setError("No lab ID found. Please login again.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `https://hms.backend.hiragroupcloud.com/api/account/lab-accounts/${labId}`
      );

      if (response.data.success) {
        const normalized = (response.data.data || [])
          .filter((account) => account && account.account_name)
          .map((account) => ({
            ...account,
            accountId: account.accountId || "N/A",
            account_name: account.account_name || "Unnamed",
            address: account.address || "N/A",
            address2: account.address2 || "N/A",
            city: account.city || "N/A",
            state: account.state || "N/A",
            zip: account.zip || "N/A",
            country: account.country || "N/A",
            telephone: account.telephone || "N/A",
            email: account.email || "N/A",
            website: account.website || "N/A",
            time_zone: account.time_zone || "N/A",
            account_type: account.account_type || "N/A",
            status: account.status || "Active",
            addedBy: account.addedBy || "Unknown",
            source: "HMS",
          }));

        setRows(normalized);
      } else {
        setError(response.data.message || "Failed to load accounts.");
      }
    } catch (err) {
      console.error("Fetch Accounts Error:", err);
      setError("Failed to load accounts. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // FETCH ACCOUNTS WHEN LAB ID IS AVAILABLE
  // =========================================================

  useEffect(() => {
    if (labId) {
      fetchAccounts();
    }
  }, [labId]);

  // =========================================================
  // DELETE ACCOUNT
  // =========================================================

  const handleDelete = async (id, source) => {
    const accountToDelete = rows.find((row) => row._id === id);

    if (accountToDelete && accountToDelete.addedBy !== labId) {
      Swal.fire({
        icon: "error",
        title: "Permission Denied",
        text: "You can only delete accounts created by your lab.",
        confirmButtonColor: "#004aad",
      });
      return;
    }

    const result = await Swal.fire({
      title: "<span style='color:#004aad; font-weight:bold;'>Confirm Delete</span>",
      html: `
        Are you sure you want to delete account
        "<strong>${accountToDelete?.account_name || "this account"}</strong>"?
      `,
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      buttonsStyling: false,
      customClass: {
        popup: "custom-swal-popup",
        confirmButton: "custom-swal-confirm",
        cancelButton: "custom-swal-cancel",
        title: "custom-swal-title",
        htmlContainer: "custom-swal-html",
      },
      width: "450px",
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      let response;

      if (source === "HMS") {
        response = await axios.delete(
          `https://hms.backend.hiragroupcloud.com/api/account/deleteAccount/${id}`
        );
      }

      if (response?.data?.success || response?.status === 200) {
        setRows((prev) => prev.filter((row) => row._id !== id));

        const Toast = Swal.mixin({
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          background: "#d4edda",
          customClass: {
            popup: "custom-toast-popup",
            title: "custom-toast-title",
          },
          didOpen: (toast) => {
            toast.addEventListener("mouseenter", Swal.stopTimer);
            toast.addEventListener("mouseleave", Swal.resumeTimer);
          },
        });

        Toast.fire({
          icon: "success",
          title: "Account deleted successfully!",
        });
      }
    } catch (error) {
      console.error("Delete Error:", error);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: "Failed to delete account. Please try again.",
        confirmButtonColor: "#004aad",
      });
    }
  };

  // =========================================================
  // EDIT ACCOUNT
  // =========================================================

  const handleEdit = (account) => {
    if (account.addedBy !== labId) {
      alert("You can only edit accounts created by your lab.");
      return;
    }

    navigate(`/TabsWorking/${account.accountId}`, {
      state: {
        accountName: account.account_name,
        accountData: account,
      },
    });
  };

  // =========================================================
  // ACTION MENU HANDLERS (Edit/Delete dropdown)
  // =========================================================

  const handleActionClick = (event, row) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setSelectedRow(null);
  };

  // =========================================================
  // MENU
  // =========================================================

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    handleClose();
  };

  // =========================================================
  // PAGINATION
  // =========================================================

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, 4, 5];
    }

    if (currentPage >= totalPages - 2) {
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      currentPage - 2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      currentPage + 2,
    ];
  };

  // =========================================================
  // RETURN (UPDATED LAYOUT)
  // =========================================================

  return (
    <>
      <Header />
      <Navvbar />
      <br />

      <div style={{ width: "95%", margin: "0 auto", padding: "0" }}>
        <div className="row">

          {/* Sidebar */}
          <div className="col-md-2 sidebar-col">
            <Sidebar />
          </div>

          {/* Main Content */}
          <div className="col-md-10 main-content-col">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="accounts-main-heading" style={{ color: "#003b78", fontSize: "30px", fontWeight: "400", margin: "0" }}>Accounts</h2>
            </div>

            <div className="account-main-box">
              
              {/* Search + Add */}
              <div className="account-controls">
                <div className="search-section">
                  <span className="search-label">Search</span>
                  <TextField
                    className="account-search-input"
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    placeholder="Type to Search"
                    InputProps={{
                      startAdornment: (<InputAdornment position="start"></InputAdornment>),
                      style: { height: "100%" }
                    }}
                  />
                </div>

                {/* ===== ADD BUTTON WITH FIXED DROPDOWN ===== */}
                <div className="add-section">
                  <ButtonGroup variant="contained" disableElevation>
                    <Button
                      onClick={() => navigate("/SearchAddPage")}
                      className="add-account-btn"
                    >
                      <FaPlus />
                      Add
                    </Button>
                    <Button
                      size="small"
                      aria-controls={Boolean(anchorEl) ? "split-button-menu" : undefined}
                      aria-expanded={Boolean(anchorEl) ? "true" : undefined}
                      aria-haspopup="menu"
                      onClick={handleClick}
                      className="add-arrow-btn"
                    >
                      <MdArrowDropDown style={{ fontSize: "23px" }} />
                    </Button>
                  </ButtonGroup>

                  <Menu id="split-button-menu" className="account-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                    <MenuItem onClick={() => handleMenuItemClick("/assign")} style={{ color: "#004aad", fontWeight: "bold" }}><FaPlus style={{ marginRight: "8px" }} />Assign Price List Groups</MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("/assign-procedure")} style={{ color: isDisabled ? "grey" : "initial", pointerEvents: isDisabled ? "none" : "auto" }}><FaPlus style={{ marginRight: "8px" }} />Assign Procedure Groups</MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("/assign-shipping")} style={{ color: "#004aad", fontWeight: "bold" }}><FaPlus style={{ marginRight: "8px" }} />Assign Shipping Services</MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("/assign-tags")} style={{ color: isDisabled ? "grey" : "initial", pointerEvents: isDisabled ? "none" : "auto" }}><FaPlus style={{ marginRight: "8px" }} />Assign Account Tags</MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("/account-default")} style={{ color: isDisabled ? "grey" : "initial", pointerEvents: isDisabled ? "none" : "auto" }}><FaPlus style={{ marginRight: "8px" }} />New Account Defaults</MenuItem>
                    <MenuItem onClick={() => handleMenuItemClick("/account-quick")} style={{ color: isDisabled ? "grey" : "initial", pointerEvents: isDisabled ? "none" : "auto" }}><FiPrinter style={{ marginRight: "8px" }} />Account Quick Codes</MenuItem>
                    <MenuItem onClick={handleExportToExcel} style={{ color: "#004aad", fontWeight: "bold" }}><TbFileDownload style={{ marginRight: "8px" }} />Export to Excel</MenuItem>
                    <MenuItem onClick={handleExportBillingAddresses} style={{ color: "#004aad", fontWeight: "bold" }}><TbFileDownload style={{ marginRight: "8px" }} />Export Billing Addresses</MenuItem>
                    <MenuItem onClick={handleExportShippingAddresses} style={{ color: "#004aad", fontWeight: "bold" }}><TbFileDownload style={{ marginRight: "8px" }} />Export Shipping Addresses</MenuItem>
                  </Menu>
                </div>
              </div>

              {/* Content */}
              {loading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}><CircularProgress sx={{ color: "#004aad" }} /></div>
              ) : error ? (
                <div style={{ padding: "20px 0" }}><Alert severity="error">{error}</Alert></div>
              ) : rows.length === 0 ? (
                <div style={{ padding: "20px 0" }}><Alert severity="info">No accounts found for your lab.</Alert></div>
              ) : (
                <>
                  <div className="entries-text-top">Showing {startIndex + 1} to {endIndex} of {totalEntries} entries</div>

                  <div className="account-table-wrapper">
                    <table className="custom-account-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>City</th>
                          <th>State</th>
                          <th>Country</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th className="action-header">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedRows.map((row) => (
                          <tr key={row._id}>
                            <td>{row.account_name}</td>
                            <td>{row.city}</td>
                            <td>{row.state}</td>
                            <td>{row.country}</td>
                            <td>{row.account_type}</td>
                            <td>{row.status}</td>
                            <td style={{ textAlign: 'center' }}>
                            <ButtonGroup
                              variant="contained"
                              disableElevation
                              className="edit-action-group"
                            >
                              <Button
                                size="small"
                                onClick={() => handleEdit(row)}
                                className="edit-action-btn"
                              >
                                Edit
                              </Button>

                              <Button
                                size="small"
                                onClick={(e) => handleActionClick(e, row)}
                                className="edit-action-arrow-btn"
                                aria-label="More actions"
                              >
                                <MdArrowDropDown />
                              </Button>
                            </ButtonGroup>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <Menu
                      className="account-menu"
                      anchorEl={actionAnchorEl}
                      open={Boolean(actionAnchorEl)}
                      onClose={handleActionClose}
                    >
                      <MenuItem
                        onClick={() => {
                          handleDelete(selectedRow._id, selectedRow.source);
                          handleActionClose();
                        }}
                        style={{
                          color: "#dc3545",
                          fontSize: "13px",
                        }}
                      >
                        <MdDelete
                          style={{
                            marginRight: "8px",
                            fontSize: "18px",
                          }}
                        />
                        Delete
                      </MenuItem>
                    </Menu>
                  </div>

                  {/* Pagination */}
                  <div className="pagination-footer">
                    <div className="pagination-info">Showing {startIndex + 1} to {endIndex} of {totalEntries} entries</div>
                    <div className="pagination-buttons">
                      <button type="button" className="pagination-btn" onClick={() => goToPage(1)} disabled={currentPage === 1}>First</button>
                      <button type="button" className="pagination-btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
                      {getPageNumbers().map((page) => (
                        <button type="button" key={page} className={`pagination-btn ${currentPage === page ? "active" : ""}`} onClick={() => goToPage(page)}>{page}</button>
                      ))}
                      <button type="button" className="pagination-btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
                      <button type="button" className="pagination-btn" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="page-footer-wrapper"><Footer /></div>
    </>
  );
};

export default AccountTable;